import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStar,
  faEnvelope,
  faCopy,
  faCheck,
  faRotateRight,
  faEye,
  faEyeSlash,
  faLock,
  faFire,
  faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import './AdminParentFeedback.css';

/* =========================================================
   Admin › Parent feedback
   - Answers: what parents said (+ show on homepage; every comment can be shown)
   - Sent: emailed, not opened yet
   - Opened: opened the email or the form, but didn't answer
   - Not sent: link created but no email went out (WhatsApp only, or the email failed)
   - Resend: emailed over 3 days ago, still not opened: time to send again
   - Ask parents: paid students whose parents weren't asked yet
   Demo parents get the form automatically 30 min after the demo.
   ========================================================= */
const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });
const TZ = 'Asia/Beirut';

const digits = (phone) => String(phone || '').replace(/\D/g, '');
const waUrl = (phone, text) =>
  `https://api.whatsapp.com/send/?phone=${digits(phone)}${text ? `&text=${encodeURIComponent(text)}` : ''}&type=phone_number&app_absent=0`;

const TABS = [
  ['answers', 'Answers'],
  ['sent', 'Sent'],
  ['opened', 'Opened'],
  ['notsent', 'Not sent'],
  ['resend', 'Resend'],
  ['ask', 'Ask parents'],
];

const EMPTY = {
  sent: 'No emails waiting to be opened.',
  opened: 'Nobody opened without answering. 🎉',
  notsent: 'Every request was emailed.',
  resend: 'Nothing to resend right now. 🎉',
};

// status of a request that wasn't answered yet
const RESEND_AFTER_H = 72; // resend when emailed over 3 days ago and still not opened
const openedAt = (r) => r.EmailOpenedAt || r.OpenedAt;
const statusOf = (r) => {
  if (!Number(r.SendCount)) return 'notsent';
  if (openedAt(r)) return 'opened';
  return 'sent';
};
const dueToResend = (r) =>
  statusOf(r) === 'sent' && r.LastSentAt && Date.now() - new Date(r.LastSentAt).getTime() >= RESEND_AFTER_H * 3600000;

const KINDS = [
  ['all', 'All'],
  ['demo', 'Demo'],
  ['paid', 'Paid'],
];

const dateOf = (v) => {
  const d = v ? new Date(v) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min < 60) return min < 1 ? 'just now' : `${min} min ago`;
  if (min < 24 * 60) return `${Math.floor(min / 60)}h ago`;
  return d.toLocaleDateString('en-US', { timeZone: TZ, month: 'short', day: 'numeric', year: 'numeric' });
};

// Schedule times are Lebanon wall-clock digits: read them as-is
const classDate = (v) => {
  const d = v ? new Date(v) : null;
  if (!d || Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
};

const StarRow = ({ value }) =>
  value ? (
    <span className="rmpfa-stars" aria-label={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={n <= value ? 'is-on' : ''} aria-hidden="true">
          ★
        </span>
      ))}
    </span>
  ) : (
    <span className="rmpfa-muted">—</span>
  );

const recTone = (n) => (n === null || n === undefined ? '' : n >= 9 ? 'tone-green' : n >= 7 ? 'tone-amber' : 'tone-red');
const INTENT = { yes: ['Wants to enroll', 'tone-green'], maybe: ['Maybe later', 'tone-amber'], no: ['Not for now', 'tone-muted'] };

const AdminParentFeedback = () => {
  const [tab, setTab] = useState('answers');
  const [kind, setKind] = useState('all');
  const [data, setData] = useState({ items: [], stats: null });
  const [candidates, setCandidates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null); // id / studentId being worked on
  const [toast, setToast] = useState('');
  const [query, setQuery] = useState('');
  const [bulk, setBulk] = useState(null); // { kind, total, done, sent, failed, finished }

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/api/admin/parentfeedback?kind=${kind}`, auth());
      setData({ items: res.data?.items || [], stats: res.data?.stats || null });
    } catch {
      setError("Couldn't load the feedback. Please refresh.");
    } finally {
      setLoading(false);
    }
  }, [kind]);

  const loadCandidates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/parentfeedback/candidates`, auth());
      setCandidates(res.data?.items || []);
    } catch {
      setCandidates([]);
      setError("Couldn't load the students list.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (tab === 'ask' && candidates === null) loadCandidates();
  }, [tab, candidates, loadCandidates]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const answers = useMemo(() => data.items.filter((x) => x.SubmittedAt), [data.items]);
  const waiting = useMemo(() => data.items.filter((x) => !x.SubmittedAt), [data.items]);
  const lists = useMemo(
    () => ({
      sent: waiting.filter((x) => statusOf(x) === 'sent'),
      opened: waiting.filter((x) => statusOf(x) === 'opened'),
      notsent: waiting.filter((x) => statusOf(x) === 'notsent'),
      resend: waiting.filter(dueToResend),
    }),
    [waiting]
  );
  const q = query.trim().toLowerCase();
  const match = (name) => !q || String(name || '').toLowerCase().includes(q);

  const replaceItem = (item) =>
    setData((d) => {
      const exists = d.items.some((x) => x.Id === item.Id);
      return { ...d, items: exists ? d.items.map((x) => (x.Id === item.Id ? item : x)) : [item, ...d.items] };
    });

  const resend = async (row) => {
    setBusy(row.Id);
    try {
      const res = await axios.post(`${API}/api/admin/parentfeedback/${row.Id}/resend`, {}, auth());
      replaceItem(res.data.item);
      setToast(`Email sent again to ${row.ParentEmail}`);
    } catch (err) {
      setToast(err.response?.data?.error || "Couldn't send the email.");
    } finally {
      setBusy(null);
    }
  };

  const whatsapp = (row) => {
    if (!digits(row.MobileNumber)) {
      setToast('No mobile number for this parent.');
      return;
    }
    window.open(waUrl(row.MobileNumber, row.WhatsappText), '_blank', 'noopener');
  };

  const copyLink = async (row) => {
    try {
      await navigator.clipboard.writeText(row.Link);
      setToast('Link copied');
    } catch {
      setToast(row.Link);
    }
  };

  const toggleShow = async (row) => {
    setBusy(row.Id);
    try {
      await axios.put(`${API}/api/admin/parentfeedback/${row.Id}/show`, { show: !row.ShowOnSite }, auth());
      replaceItem({ ...row, ShowOnSite: !row.ShowOnSite });
      setToast(row.ShowOnSite ? 'Hidden from the homepage' : 'Now shown on the homepage');
    } catch (err) {
      setToast(err.response?.data?.error || "Couldn't update.");
    } finally {
      setBusy(null);
    }
  };

  // ---------- Send to everyone in the list (one by one, a short pause between emails) ----------
  const RESEND_GAP_H = 72; // don't email the same parent again within 3 days
  const recentlyEmailed = (r) => r.LastSentAt && Date.now() - new Date(r.LastSentAt).getTime() < RESEND_GAP_H * 3600000;

  const runBulk = async (kind, list, sendOne) => {
    setBulk({ kind, total: list.length, done: 0, sent: 0, failed: 0, finished: false });
    let sent = 0;
    let failed = 0;
    for (let i = 0; i < list.length; i += 1) {
      try {
        await sendOne(list[i]);
        sent += 1;
      } catch {
        failed += 1;
      }
      setBulk({ kind, total: list.length, done: i + 1, sent, failed, finished: false });
      if (i < list.length - 1) await new Promise((r) => setTimeout(r, 1500));
    }
    setBulk({ kind, total: list.length, done: list.length, sent, failed, finished: true });
    setToast(`Done: ${sent} email${sent === 1 ? '' : 's'} sent${failed ? `, ${failed} failed` : ''}.`);
  };

  const resendAll = (list, kind = 'resend') => {
    const due = list.filter((r) => !recentlyEmailed(r));
    if (!due.length) {
      setToast('Everyone here was emailed in the last 3 days.');
      return;
    }
    const skipped = list.length - due.length;
    if (!window.confirm(`Email the feedback form${kind === 'resend' ? ' again' : ''} to ${due.length} parent${due.length === 1 ? '' : 's'}?${skipped ? `\n(${skipped} emailed in the last 3 days are skipped.)` : ''}`)) return;
    runBulk(kind, due, async (r) => {
      const res = await axios.post(`${API}/api/admin/parentfeedback/${r.Id}/resend`, {}, auth());
      replaceItem(res.data.item);
    });
  };

  const askAll = (list) => {
    if (!list.length) return;
    if (!window.confirm(`Email the feedback form to ${list.length} parent${list.length === 1 ? '' : 's'}? Each parent is asked only once.`)) return;
    runBulk('ask', list, async (c) => {
      const res = await axios.post(`${API}/api/admin/parentfeedback/send`, { studentId: c.IdUser, email: true }, auth());
      replaceItem(res.data.item);
      setCandidates((cur) => (cur || []).filter((x) => x.IdUser !== c.IdUser));
    });
  };

  const bulkBar = (kind, label, onClick, count) => (
    <div className="rmpfa-bulk">
      {bulk && bulk.kind === kind ? (
        <>
          <span className="rmpfa-bulk-text">
            {bulk.finished
              ? `Done: ${bulk.sent} sent${bulk.failed ? `, ${bulk.failed} failed` : ''}.`
              : `Sending ${bulk.done} of ${bulk.total}… keep this page open.`}
          </span>
          <span className="rmpfa-bulk-meter" aria-hidden="true">
            <i style={{ width: `${bulk.total ? (bulk.done / bulk.total) * 100 : 0}%` }} />
          </span>
          {bulk.finished && (
            <button type="button" className="rmpfa-btn rmpfa-btn--ghost" onClick={() => setBulk(null)}>
              OK
            </button>
          )}
        </>
      ) : (
        <>
          <button type="button" className="rmpfa-btn" onClick={onClick} disabled={!count || (bulk && !bulk.finished)}>
            <FontAwesomeIcon icon={faEnvelope} /> {label} ({count})
          </button>
          {!count && kind === 'resend' && <span className="rmpfa-muted">Everyone here was emailed in the last 3 days.</span>}
          {kind === 'resend' && count > 0 && <span className="rmpfa-muted">Parents emailed in the last 3 days are skipped.</span>}
        </>
      )}
    </div>
  );

  // Ask parents: by email, or create the link and open WhatsApp
  const ask = async (st, viaEmail) => {
    if (!viaEmail && !digits(st.MobileNumber)) {
      setToast('No mobile number for this parent.');
      return;
    }
    const win = viaEmail ? null : window.open('', '_blank'); // open now, so the browser doesn't block it
    setBusy(st.IdUser);
    try {
      const res = await axios.post(`${API}/api/admin/parentfeedback/send`, { studentId: st.IdUser, email: viaEmail }, auth());
      const item = res.data.item;
      replaceItem(item);
      setCandidates((list) => (list || []).filter((c) => c.IdUser !== st.IdUser));
      if (win) win.location.href = waUrl(st.MobileNumber || item.MobileNumber, item.WhatsappText);
      setToast(viaEmail ? `Email sent to ${item.ParentEmail}` : 'WhatsApp opened with the link');
    } catch (err) {
      if (win) win.close();
      setToast(err.response?.data?.error || "Couldn't send.");
    } finally {
      setBusy(null);
    }
  };

  const s = data.stats;

  return (
    <div className="rmpfa">
      <header className="rmpfa-header">
        <div>
          <h1>Parent feedback</h1>
          <p>What parents think about the academy. Demo parents get the form automatically after the demo.</p>
        </div>
        <button type="button" className="rmpfa-icon-btn" onClick={load} aria-label="Refresh" title="Refresh">
          <FontAwesomeIcon icon={faRotateRight} />
        </button>
      </header>

      {s && (
        <section className="rmpfa-kpis">
          <div className="rmpfa-kpi">
            <span>Academy</span>
            <strong>
              {s.avgAcademy ?? '—'} <FontAwesomeIcon icon={faStar} className="rmpfa-kpi-star" />
            </strong>
            <small>average of {s.answered} answers</small>
          </div>
          <div className="rmpfa-kpi">
            <span>Teachers</span>
            <strong>
              {s.avgTeacher ?? '—'} <FontAwesomeIcon icon={faStar} className="rmpfa-kpi-star" />
            </strong>
            <small>average rating</small>
          </div>
          <div className="rmpfa-kpi" title="% who would surely recommend (9-10) minus % who wouldn't (0-6)">
            <span>Recommend score</span>
            <strong className={s.nps === null ? '' : s.nps >= 50 ? 'is-good' : s.nps >= 0 ? 'is-ok' : 'is-bad'}>
              {s.nps === null ? '—' : s.nps > 0 ? `+${s.nps}` : s.nps}
            </strong>
            <small>from −100 to +100</small>
          </div>
          <div className="rmpfa-kpi">
            <span>Answered</span>
            <strong>{s.responseRate}%</strong>
            <small>
              {s.answered} of {s.sent} parents
            </small>
          </div>
          <div className="rmpfa-kpi is-hot">
            <span>
              <FontAwesomeIcon icon={faFire} /> Want to enroll
            </span>
            <strong>{s.wantEnroll}</strong>
            <small>demo parents who said yes</small>
          </div>
        </section>
      )}

      <div className="rmpfa-bar">
        <div className="rmpfa-tabs" role="tablist" aria-label="Feedback">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={tab === key ? 'is-on' : ''}
              onClick={() => setTab(key)}
            >
              {label}
              <span>
                {key === 'answers' ? answers.length : lists[key] ? lists[key].length : candidates ? candidates.length : '…'}
              </span>
            </button>
          ))}
        </div>
        <div className="rmpfa-filters">
          {tab !== 'ask' && (
            <div className="rmpfa-seg" role="radiogroup" aria-label="Type">
              {KINDS.map(([key, label]) => (
                <button key={key} type="button" role="radio" aria-checked={kind === key} className={kind === key ? 'is-on' : ''} onClick={() => setKind(key)}>
                  {label}
                </button>
              ))}
            </div>
          )}
          <label className="rmpfa-search">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <input type="search" placeholder="Search student" value={query} onChange={(e) => setQuery(e.target.value)} />
          </label>
        </div>
      </div>

      {error && <p className="rmpfa-error">{error}</p>}
      {loading && !data.items.length && <div className="rmpfa-skel" aria-hidden="true"><span /><span /></div>}

      {/* ---------- Answers ---------- */}
      {tab === 'answers' && !loading && (
        answers.filter((r) => match(r.StudentName)).length === 0 ? (
          <p className="rmpfa-empty">No answers yet.</p>
        ) : (
          <ul className="rmpfa-list">
            {answers.filter((r) => match(r.StudentName)).map((r) => (
              <li key={r.Id} className="rmpfa-answer">
                <div className="rmpfa-answer-head">
                  <div>
                    <strong>{r.StudentName || 'Student'}</strong>
                    <span className="rmpfa-sub">
                      <span className={`rmpfa-kind is-${r.Kind}`}>{r.Kind === 'demo' ? 'Demo' : 'Paid'}</span>
                      {dateOf(r.SubmittedAt)} · {r.Lang === 'ar' ? 'Arabic' : 'English'}
                    </span>
                  </div>
                  <div className="rmpfa-scores">
                    <span className="rmpfa-score">
                      <small>Academy</small>
                      <StarRow value={r.RatingAcademy} />
                    </span>
                    <span className="rmpfa-score">
                      <small>Teacher</small>
                      <StarRow value={r.RatingTeacher} />
                    </span>
                    <span className={`rmpfa-pill ${recTone(r.Recommend)}`}>{r.Recommend}/10</span>
                    {r.EnrollIntent && INTENT[r.EnrollIntent] && (
                      <span className={`rmpfa-pill ${INTENT[r.EnrollIntent][1]}`}>
                        {r.EnrollIntent === 'yes' && <FontAwesomeIcon icon={faFire} />} {INTENT[r.EnrollIntent][0]}
                      </span>
                    )}
                  </div>
                </div>

                {(r.Liked || r.Improve) && (
                  <div className="rmpfa-quotes">
                    {r.Liked && (
                      <blockquote className="is-liked" dir="auto">
                        <small>Liked</small>
                        {r.Liked}
                      </blockquote>
                    )}
                    {r.Improve && (
                      <blockquote className="is-improve" dir="auto">
                        <small>To improve</small>
                        {r.Improve}
                      </blockquote>
                    )}
                  </div>
                )}

                <div className="rmpfa-answer-foot">
                  {r.Liked ? (
                    <>
                      <span className="rmpfa-muted">
                        On the website as <b>{r.DisplayName || 'A RoboMind parent'}</b>
                      </span>
                      <button
                        type="button"
                        className={`rmpfa-btn ${r.ShowOnSite ? 'rmpfa-btn--on' : 'rmpfa-btn--ghost'}`}
                        onClick={() => toggleShow(r)}
                        disabled={busy === r.Id}
                      >
                        <FontAwesomeIcon icon={r.ShowOnSite ? faEye : faEyeSlash} />
                        {r.ShowOnSite ? 'Shown on homepage' : 'Show on homepage'}
                      </button>
                    </>
                  ) : (
                    <span className="rmpfa-muted">
                      <FontAwesomeIcon icon={faLock} /> No comment to show on the website
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )
      )}

      {/* ---------- Sent / Opened / Not sent / Resend ---------- */}
      {lists[tab] && !loading && (
        lists[tab].filter((r) => match(r.StudentName)).length === 0 ? (
          <p className="rmpfa-empty">{EMPTY[tab]}</p>
        ) : (
          <>
          {tab === 'resend' &&
            bulkBar(
              'resend',
              'Resend to all by email',
              () => resendAll(lists.resend.filter((r) => match(r.StudentName))),
              lists.resend.filter((r) => match(r.StudentName) && !recentlyEmailed(r)).length
            )}
          {tab === 'notsent' &&
            bulkBar(
              'notsent',
              'Email all',
              () => resendAll(lists.notsent.filter((r) => match(r.StudentName)), 'notsent'),
              lists.notsent.filter((r) => match(r.StudentName)).length
            )}
          {tab === 'opened' && (
            <p className="rmpfa-note">They opened it but didn't answer. A short WhatsApp message usually works best here.</p>
          )}
          <ul className="rmpfa-rows">
            {lists[tab].filter((r) => match(r.StudentName)).map((r) => (
              <li key={r.Id}>
                <div className="rmpfa-row-main">
                  <strong>{r.StudentName || 'Student'}</strong>
                  <span className="rmpfa-sub">
                    <span className={`rmpfa-kind is-${r.Kind}`}>{r.Kind === 'demo' ? 'Demo' : 'Paid'}</span>
                    {r.SendCount ? `Emailed ${r.SendCount}× · last ${dateOf(r.LastSentAt)}` : 'Not emailed yet'}
                    {' · '}
                    {r.OpenedAt ? (
                      <b className="rmpfa-opened">Opened the form {dateOf(r.OpenedAt)}</b>
                    ) : r.EmailOpenedAt ? (
                      <b className="rmpfa-opened">Opened the email {dateOf(r.EmailOpenedAt)}</b>
                    ) : r.SendCount ? (
                      'Not opened yet'
                    ) : null}
                  </span>
                </div>
                <div className="rmpfa-actions">
                  <button type="button" className="rmpfa-btn rmpfa-btn--ghost" onClick={() => resend(r)} disabled={busy === r.Id} title={r.ParentEmail}>
                    <FontAwesomeIcon icon={faEnvelope} /> <span>{r.SendCount ? 'Resend' : 'Send email'}</span>
                  </button>
                  <button type="button" className="rmpfa-btn rmpfa-btn--wa" onClick={() => whatsapp(r)} title="Send on WhatsApp">
                    <FontAwesomeIcon icon={faWhatsapp} /> <span>WhatsApp</span>
                  </button>
                  <button type="button" className="rmpfa-icon-btn" onClick={() => copyLink(r)} aria-label="Copy link" title="Copy link">
                    <FontAwesomeIcon icon={faCopy} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
          </>
        )
      )}

      {/* ---------- Ask parents ---------- */}
      {tab === 'ask' && (
        candidates === null ? (
          <div className="rmpfa-skel" aria-hidden="true"><span /><span /></div>
        ) : candidates.filter((c) => match(`${c.FirstName} ${c.LastName}`)).length === 0 ? (
          <p className="rmpfa-empty">
            <FontAwesomeIcon icon={faCheck} /> Every paid student's parent has been asked.
          </p>
        ) : (
          <>
            <p className="rmpfa-note">Paid students whose parents haven't been asked yet. Each parent is asked only once.</p>
            {bulkBar('ask', 'Email all', () => askAll(candidates.filter((c) => match(`${c.FirstName} ${c.LastName}`))), candidates.filter((c) => match(`${c.FirstName} ${c.LastName}`)).length)}
            <ul className="rmpfa-rows">
              {candidates.filter((c) => match(`${c.FirstName} ${c.LastName}`)).map((c) => (
                <li key={c.IdUser}>
                  <div className="rmpfa-row-main">
                    <strong>
                      {c.FirstName} {c.LastName}
                    </strong>
                    <span className="rmpfa-sub">
                      {c.ClassesDone} classes done · last {classDate(c.LastClass)}
                    </span>
                  </div>
                  <div className="rmpfa-actions">
                    <button type="button" className="rmpfa-btn" onClick={() => ask(c, true)} disabled={busy === c.IdUser} title={c.ParentsEmail}>
                      <FontAwesomeIcon icon={faEnvelope} /> <span>Email</span>
                    </button>
                    <button type="button" className="rmpfa-btn rmpfa-btn--wa" onClick={() => ask(c, false)} disabled={busy === c.IdUser}>
                      <FontAwesomeIcon icon={faWhatsapp} /> <span>WhatsApp</span>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )
      )}

      {toast && (
        <div className="rmpfa-toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
};

export default AdminParentFeedback;
